// Canvas-2D acoustic charts — a faithful reconstruction of the legacy /admin
// charts (`drawConsumerFreqBalance`, `_drawSubRT60Chart`, `_drawAboveRT60Chart`).
//
// All axis/tick/window/path math comes from `@roomtreatment/charts` — this
// file is the paint layer only (design-token ink colors, empty states, the
// smoothing/target-band choices specific to each chart). Mobile's Skia
// components paint the exact same geometry, so a fix to the shared math
// (log-tick placement, dB windowing, …) fixes every renderer at once.
//
// Canvas is rendered transparent; the wrap's CSS background (`--ed-bg`) shows
// through.  Tying the chart well to the same token as the 3D viewport keeps
// them visually locked, so the eye never bounces between two different grounds
// when scanning between the room and its readouts.
//
// BEFORE / AFTER IS ENCODED IN INK WEIGHT, NOT IN COLOUR. The measured curve is
// full-weight ink; the untreated baseline is the faint tier, thinner. That is a
// deliberate, and deliberately costly, choice: two hues (the old blue/gold pair)
// were strictly easier to tell apart for colour-normal readers. One hue is what
// the design language asks for, it survives being printed or screenshotted in
// greyscale, and it does not fail for the ~8% of men with a red-green
// deficiency.
import { canvasTypeFont, withAlpha } from '@roomtreatment/design';
import {
  computeFreqResponseGeometry,
  computeRt60Geometry,
  computeAbsorptionGeometry,
  nearestValueAtFreq,
  logFreqToX,
  linearToPlot,
  smoothFrequencyResponse,
  evalTargetCurve,
  type ChartPad,
  type AxisTicks,
  type YTick,
  type PlotFrame,
  type PlotPoint,
  type SmoothingFraction,
} from '@roomtreatment/charts';
import { canvasInkAt } from '../canvasInk';
import { tokensAt } from '@/lib/theme/tokens';
import {
  setupChartCanvas, fitYAxisPad, drawLogFreqAxisGrid, inkLegend, EMPTY_PAINT, X_AXIS_PAD_B, type ChartPaint, chartGridInk,
} from './chartCanvas';

const MEASURED_WIDTH = 2.0;
const BASELINE_WIDTH = 1.3;

/**
 * This file's colours, resolved from the canvas being drawn at DRAW time —
 * never captured at import, which would freeze them in whatever theme loaded
 * the module (see canvasInkAt).
 */
function chartInk(canvas: HTMLCanvasElement) {
  const t = tokensAt(canvas);
  const ink = canvasInkAt(canvas);
  return {
    /** The measured, current state. */
    measured: ink.stroke,
    /** The untreated room, drawn behind it. */
    baseline: ink.label,
    label: ink.label,
    /** The one sanctioned colour on these charts: the studio target band reports
     *  an OUTCOME (is the room inside spec?), not a measurement, which is exactly
     *  the distinction `status.ok` exists for. */
    targetBand: withAlpha(t.ok, 0.12),
    ...chartGridInk(t),
    /** Target curve overlay — a reference SHAPE, not a measurement, same
     *  reasoning as targetBand: the one place a distinct visual treatment
     *  (dotted, muted) earns its keep instead of ink-weight alone. */
    targetCurve: withAlpha(t.ink, 0.55),
  };
}

// Padding is exported so every consumer (Charts.tsx, FreqResponseVerdict.tsx,
// CustomPanelDesigner.tsx) shares ONE literal per chart type instead of each
// re-declaring its own copy that has to be kept in sync by convention. A
// crosshair whose `pad` prop drifts from the pad the drawer actually used
// lines up with the wrong pixel — this is what used to happen.
//
// These are the NOMINAL pads, sized for the usual labels at the canvas tick
// font ("-30" dB, "0.00" s). `fitYAxisPad` widens the left edge for a wider
// set, and the plot descriptor each drawer returns carries the pad it really
// used — so read the frame from the paint, never re-derive it from these.
export const PAD_FREQ: ChartPad = { t: 14, r: 14, b: 34, l: 44 };
export const PAD_RT60: ChartPad = { t: 10, r: 14, b: X_AXIS_PAD_B, l: 48 };
export const PAD_ABSORPTION: ChartPad = { t: 12, r: 14, b: X_AXIS_PAD_B, l: 34 };

function measureWidth(ctx: CanvasRenderingContext2D) {
  return (label: string) => ctx.measureText(label).width;
}

function drawGrid(ctx: CanvasRenderingContext2D, frame: PlotFrame, xTicks: AxisTicks, yTicks: YTick[], xLabel = 'Hz', yLabel = 'dB') {
  const { gridMajor, gridMinor } = chartInk(ctx.canvas);
  drawLogFreqAxisGrid(ctx, frame, xTicks, yTicks, { gridMajor, gridMinor, xLabel, yLabel });
}

function drawLine(
  ctx: CanvasRenderingContext2D, frame: PlotFrame, pts: PlotPoint[],
  color: string, width: number, smooth: boolean,
) {
  if (pts.length < 2) return;
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(frame.plotX + pts[0]!.x, frame.plotY + pts[0]!.y);
  if (smooth) {
    for (let i = 1; i < pts.length - 1; i++) {
      const xc = frame.plotX + (pts[i]!.x + pts[i + 1]!.x) / 2;
      const yc = frame.plotY + (pts[i]!.y + pts[i + 1]!.y) / 2;
      ctx.quadraticCurveTo(frame.plotX + pts[i]!.x, frame.plotY + pts[i]!.y, xc, yc);
    }
    const last = pts[pts.length - 1]!;
    ctx.lineTo(frame.plotX + last.x, frame.plotY + last.y);
  } else {
    for (let i = 1; i < pts.length; i++) ctx.lineTo(frame.plotX + pts[i]!.x, frame.plotY + pts[i]!.y);
  }
  ctx.stroke();
}

// ── Frequency response — a dB viewport, never normalised ─────────────────
export interface FreqResponseChart {
  freqs: number[];
  mag: number[];
  magBaseline?: number[] | null;
  /** Fractional-octave smoothing applied to both traces before plotting —
   *  a pure pre-processing step, see packages/charts/smoothing.ts. Default
   *  'off'. */
  smoothing?: SmoothingFraction;
  /** Id into TARGET_CURVE_REGISTRY (packages/charts/targetCurves.ts), or
   *  null/undefined for no overlay. */
  targetCurveId?: string | null;
}

export function drawFreqResponse(
  canvas: HTMLCanvasElement, chart: FreqResponseChart,
): ChartPaint {
  const s = setupChartCanvas(canvas);
  if (!s) return EMPTY_PAINT;
  const { ctx, W, H } = s;
  const ink = chartInk(canvas);

  const smoothing = chart.smoothing ?? 'off';
  const mag = smoothFrequencyResponse(chart.freqs, chart.mag, smoothing);
  const magBaseline = chart.magBaseline?.length
    ? smoothFrequencyResponse(chart.freqs, chart.magBaseline, smoothing)
    : chart.magBaseline;

  const geo = fitYAxisPad(ctx, PAD_FREQ, (pad) => computeFreqResponseGeometry({
    freqs: chart.freqs,
    mag,
    magBaseline,
    width: W,
    height: H,
    pad,
    measureWidth: measureWidth(ctx),
  }), (g) => ({ left: g.yTicks.map((t) => t.label) }));

  if (geo.empty) {
    // The frequency axis still exists without a curve: this chart doubles as
    // the sound-pressure scrubber (see ModalFreqScrubber.tsx), and the field
    // it scrubs can be on screen before the seat response has been solved.
    // So the axis is drawn and handed back, and only the curve is missing.
    drawGrid(ctx, geo.frame, geo.xTicks, []);
    ctx.fillStyle = ink.label; ctx.textAlign = 'center'; ctx.font = canvasTypeFont('label');
    ctx.fillText('No frequency response data yet', W / 2, H / 2);
    return { caption: '', plot: { frame: geo.frame, x: { scale: geo.xScale } } };
  }

  drawGrid(ctx, geo.frame, geo.xTicks, geo.yTicks);

  if (chart.targetCurveId) {
    const targetDb = evalTargetCurve(chart.targetCurveId, chart.freqs);
    if (targetDb) {
      const targetPoints: PlotPoint[] = chart.freqs.map((f, i) => ({
        x: logFreqToX(geo.xScale, f),
        y: linearToPlot(geo.yScale, targetDb[i]!),
      }));
      ctx.setLineDash([4, 3]);
      drawLine(ctx, geo.frame, targetPoints, ink.targetCurve, 1.3, false);
      ctx.setLineDash([]);
    }
  }

  if (geo.baselinePoints) {
    drawLine(ctx, geo.frame, geo.baselinePoints, ink.baseline, BASELINE_WIDTH, false);
  }
  drawLine(ctx, geo.frame, geo.activePoints, ink.measured, MEASURED_WIDTH, false);

  return {
    caption: geo.baselinePoints ? inkLegend(canvas, 'treated', 'untreated') : '',
    plot: { frame: geo.frame, x: { scale: geo.xScale }, y: { scale: geo.yScale } },
  };
}

// ── Absorption coefficient — α (0..1) over a log-frequency axis ──────────
export interface AbsorptionChart {
  freqs: number[];
  alpha: number[];
  /** Omit to auto-fit the axis to `freqs`' actual extent. */
  fMin?: number;
  fMax?: number;
  /** The panel's own colour — material data the user picked, not a UI token. */
  accent: string;
}

export function drawAbsorption(canvas: HTMLCanvasElement, chart: AbsorptionChart): ChartPaint {
  const s = setupChartCanvas(canvas);
  if (!s) return EMPTY_PAINT;
  const { ctx, W, H } = s;
  if (!chart.freqs.length) return EMPTY_PAINT;

  const geo = computeAbsorptionGeometry({
    freqs: chart.freqs,
    alpha: chart.alpha,
    fMin: chart.fMin,
    fMax: chart.fMax,
    width: W,
    height: H,
    pad: PAD_ABSORPTION,
    measureWidth: measureWidth(ctx),
  });
  if (geo.empty) return EMPTY_PAINT;

  drawGrid(ctx, geo.frame, geo.xTicks, geo.yTicks, 'Hz', 'α');
  // MEASURED_WIDTH, not a one-off 1.8: this curve used to read paler than
  // ToolFreqChart's (cohesion audit #12) — the deeper cause was an
  // unresolvable `var(--rt-accent)` string reaching ctx.strokeStyle (see
  // AbsorptionCanvas.tsx), but matching the same weight every other
  // "measured" curve on the site uses closes the gap for real.
  drawLine(ctx, geo.frame, geo.points, chart.accent, MEASURED_WIDTH, true);

  const plot = { frame: geo.frame, x: { scale: geo.xScale }, y: { scale: geo.yScale, format: (v: number) => v.toFixed(2) } };
  if (!geo.peak) return { caption: '', plot };
  return { caption: `peak α ${geo.peak.alpha.toFixed(2)} @ ${Math.round(geo.peak.freq)} Hz`, plot };
}

// ── RT60 — normalised against a 0 s → ceiling axis ───────────────────────
export interface RT60Chart {
  points: [number, number][];
  baseline?: [number, number][] | null;
  fMin: number;
  fMax: number;
  smooth: boolean;
  band: 'sub' | 'above';
}

export function drawRT60(canvas: HTMLCanvasElement, chart: RT60Chart): ChartPaint {
  const s = setupChartCanvas(canvas);
  if (!s) return EMPTY_PAINT;
  const { ctx, W, H } = s;
  const ink = chartInk(canvas);
  const { points, baseline, fMin, fMax, smooth, band } = chart;
  if (!points.length) {
    ctx.fillStyle = ink.label; ctx.textAlign = 'center'; ctx.font = canvasTypeFont('label');
    ctx.fillText('No data yet', W / 2, H / 2);
    return EMPTY_PAINT;
  }

  const freqs = points.map((p) => p[0]);
  const values = points.map((p) => p[1]);
  const baselineFreqs = baseline?.length ? baseline.map((p) => p[0]) : null;
  const baselineValues = baseline?.length ? baseline.map((p) => p[1]) : null;

  const geo = fitYAxisPad(ctx, PAD_RT60, (pad) => computeRt60Geometry({
    freqs, values, baselineFreqs, baselineValues,
    width: W, height: H, pad, fMin, fMax,
    measureWidth: measureWidth(ctx),
  }), (g) => ({ left: g.yTicks.map((t) => t.label) }));

  if (geo.targetBandY) {
    ctx.fillStyle = ink.targetBand;
    ctx.fillRect(geo.frame.plotX, geo.frame.plotY + geo.targetBandY.y0, geo.frame.plotW, geo.targetBandY.y1 - geo.targetBandY.y0);
  }

  drawGrid(ctx, geo.frame, geo.xTicks, geo.yTicks, 'Hz', 's');

  if (geo.hasBaseline && geo.baselinePoints) {
    drawLine(ctx, geo.frame, geo.baselinePoints, ink.baseline, BASELINE_WIDTH, smooth);
  }
  drawLine(ctx, geo.frame, geo.activePoints, ink.measured, MEASURED_WIDTH, smooth);

  const plot = { frame: geo.frame, x: { scale: geo.xScale }, y: { scale: geo.yScale, format: (v: number) => v.toFixed(2) } };
  const baseTag = geo.hasBaseline ? inkLegend(canvas, 'treated', 'untreated') : '';
  if (band === 'above') {
    const a1 = nearestValueAtFreq({ x: freqs, y: values }, 1000) ?? 0;
    const a4 = nearestValueAtFreq({ x: freqs, y: values }, 4000) ?? 0;
    return { caption: `${a1.toFixed(2)}s @ 1k · ${a4.toFixed(2)}s @ 4k${baseTag ? ` · ${baseTag}` : ''}`, plot };
  }
  return { caption: baseTag, plot };
}
